import { useState, useEffect, useRef } from 'react';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (answer: string) => Promise<void>;
  questionText: string;
  isSubmitting: boolean;
  timeRemainingMs?: number | null;
}

export function AnswerInputModal({
  isOpen,
  onClose,
  onSubmit,
  questionText,
  isSubmitting,
  timeRemainingMs,
}: Props) {
  const [answer, setAnswer] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Focus textarea when modal opens
  useEffect(() => {
    if (isOpen && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isOpen]);

  // Reset answer when modal closes
  useEffect(() => {
    if (!isOpen) {
      setAnswer('');
    }
  }, [isOpen]);

  const handleSubmit = async () => {
    if (!answer.trim() || isSubmitting) return;
    await onSubmit(answer.trim());
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && e.metaKey) {
      handleSubmit();
    }
    if (e.key === 'Escape') {
      onClose();
    }
  };

  const timerSeconds = timeRemainingMs != null ? Math.ceil(timeRemainingMs / 1000) : null;
  const isTimerLow = timerSeconds != null && timerSeconds <= 5;
  const isTimerCritical = timerSeconds != null && timerSeconds <= 3;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />

      {/* Modal */}
      <div className={`relative bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden ${
        isTimerCritical ? 'ring-2 ring-red-500' : isTimerLow ? 'ring-2 ring-orange-500' : ''
      }`}>
        {/* Header */}
        <div className={`px-4 py-3 flex items-center justify-between ${
          isTimerCritical ? 'bg-red-50' : isTimerLow ? 'bg-orange-50' : 'bg-gray-50'
        }`}>
          <h3 className="font-medium text-gray-800">Type Your Answer</h3>
          <div className="flex items-center gap-3">
            {timerSeconds != null && (
              <span className={`text-sm font-bold ${
                isTimerCritical ? 'text-red-600' : isTimerLow ? 'text-orange-600' : 'text-gray-600'
              }`}>
                {timerSeconds}s remaining
              </span>
            )}
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-xl leading-none"
            >
              ×
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Question */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-sm text-blue-700">{questionText}</p>
          </div>

          {/* Answer Input */}
          <div className="space-y-2">
            <textarea
              ref={textareaRef}
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type your detailed answer here..."
              disabled={isSubmitting}
              rows={5}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
            />
            <p className="text-xs text-gray-400">Press ⌘+Enter to submit, Esc to close</p>
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            <button
              onClick={onClose}
              disabled={isSubmitting}
              className="flex-1 py-2.5 px-4 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={!answer.trim() || isSubmitting}
              className="flex-1 py-2.5 px-4 text-sm font-medium text-white bg-blue-500 rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Submitting...
                </>
              ) : (
                'Submit Answer'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
